"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import MapLibreMap from "@/components/maplibre-map";
import { HOTPLE_DEFAULT_CENTER } from "@/components/maplibre-map/config";

const toFiniteCoordinate = (value, fallback) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeCenter = (center, fallback = HOTPLE_DEFAULT_CENTER) => ({
  lat: toFiniteCoordinate(center?.lat ?? center?.latitude, fallback.lat),
  lng: toFiniteCoordinate(center?.lng ?? center?.longitude, fallback.lng),
});

const formatCoordinate = (value) => Number(value || 0).toFixed(6);

export default function MapLibreLocationPicker({
  center,
  defaultCenter = HOTPLE_DEFAULT_CENTER,
  zoom = 16,
  className = "",
  mapClassName = "",
  isInteractive = true,
  showCoordinates = true,
  showRadiusControl = false,
  radiusMeters,
  onChange,
  onIdle,
  onRadiusChange,
}) {
  const initialCenter = useMemo(
    () => normalizeCenter(center, normalizeCenter(defaultCenter)),
    [center, defaultCenter]
  );
  const [localCenter, setLocalCenter] = useState(initialCenter);
  const [localZoom, setLocalZoom] = useState(zoom);

  useEffect(() => {
    setLocalCenter(normalizeCenter(center, normalizeCenter(defaultCenter)));
  }, [center, defaultCenter]);

  useEffect(() => {
    if (Number.isFinite(Number(zoom))) setLocalZoom(Number(zoom));
  }, [zoom]);

  const updateFromCamera = useCallback(
    (camera, notify = true) => {
      const nextCenter = normalizeCenter(camera?.center, localCenter);
      setLocalCenter(nextCenter);
      if (Number.isFinite(Number(camera?.zoom))) {
        setLocalZoom(Number(camera.zoom));
      }
      if (notify && isInteractive) onChange?.(nextCenter);
      return nextCenter;
    },
    [isInteractive, localCenter, onChange]
  );

  const handleCameraChange = useCallback(
    (camera) => {
      updateFromCamera(camera, true);
    },
    [updateFromCamera]
  );

  const handleIdle = useCallback(
    (camera) => {
      const nextCenter = updateFromCamera(camera, true);
      onIdle?.({
        ...camera,
        center: nextCenter,
      });
    },
    [onIdle, updateFromCamera]
  );

  return (
    <div
      className={`relative h-full min-h-full w-full overflow-hidden ${className}`}
    >
      <MapLibreMap
        className={`rounded-md ${mapClassName}`}
        style={{ height: "100%", minHeight: "100%" }}
        mapCenter={localCenter}
        mapZoom={localZoom}
        markers={[]}
        onCameraChange={handleCameraChange}
        onIdle={handleIdle}
      />
      {!isInteractive && (
        <div
          className="absolute inset-0 z-20 bg-transparent"
          aria-hidden="true"
        />
      )}
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-30 flex -translate-x-1/2 -translate-y-full flex-col items-center">
        <div className="h-8 w-8 rounded-full border-2 border-white bg-red shadow-lg" />
        <div className="h-4 w-1 rounded-b-full bg-red shadow" />
      </div>
      {showCoordinates && (
        <div className="absolute bottom-3 right-3 z-30 rounded-md border bg-white/95 px-2 py-1 text-[11px] font-medium text-zinc-700 shadow dark:border-brown dark:bg-dark_bg_grey dark:text-white">
          {formatCoordinate(localCenter.lat)}, {formatCoordinate(localCenter.lng)}
        </div>
      )}
      {showRadiusControl && (
        <div className="absolute left-3 top-3 z-30 flex items-center gap-2 rounded-md border bg-white/95 px-3 py-2 text-xs font-medium text-zinc-700 shadow dark:border-brown dark:bg-dark_bg_grey dark:text-white">
          <span>Radius</span>
          <input
            type="number"
            min="1"
            value={radiusMeters ?? ""}
            onChange={(event) => onRadiusChange?.(Number(event.target.value))}
            className="w-24 rounded border px-2 py-1 text-xs text-black"
          />
          <span>m</span>
        </div>
      )}
    </div>
  );
}
